import React, { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Upload, X, Loader2, Image } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const FileUpload = ({ 
  value, 
  onChange, 
  placeholder = "Upload image",
  className = "",
  accept = "image/*",
  maxSizeMB = 5
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || '');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`File too large. Maximum size is ${maxSizeMB}MB`);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/api/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fileUrl = response.data.url;
      setPreview(fileUrl);
      onChange(fileUrl);
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview('');
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`relative ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
      />

      {preview ? (
        <div className="relative group">
          <div className="w-full h-32 rounded-xl overflow-hidden bg-[#16213E]/50 border border-[#2A2A4A]">
            <img 
              src={preview.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${preview}` : preview} 
              alt="Preview" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
            <Button
              type="button"
              onClick={handleClick}
              size="sm"
              className="bg-[#16213E] hover:bg-[#16213E]/80 text-white rounded-lg"
            >
              Change
            </Button>
            <Button
              type="button"
              onClick={handleRemove}
              size="sm"
              variant="destructive"
              className="rounded-lg"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleClick}
          disabled={uploading}
          className="w-full h-32 rounded-xl border-2 border-dashed border-[#2A2A4A] hover:border-[#E94560]/50 bg-[#16213E]/30 flex flex-col items-center justify-center gap-2 transition-colors"
        >
          {uploading ? (
            <>
              <Loader2 className="w-8 h-8 text-[#E94560] animate-spin" />
              <span className="text-[#8888AA] text-sm">Uploading...</span>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-[#16213E] flex items-center justify-center">
                <Image className="w-6 h-6 text-[#8888AA]" />
              </div>
              <span className="text-[#8888AA] text-sm">{placeholder}</span>
              <span className="text-[#8888AA] text-xs">PNG, JPG up to {maxSizeMB}MB</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default FileUpload;
